import {
    Box,
    Center,
    Heading,
    Spinner,
    Text,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { FaIcon } from '@acp/core-ui';
import { faBells } from '@fortawesome/pro-solid-svg-icons';
import { useNotifications } from '../hooks/use-notifications';
import { NotificationItem } from './notification-item';

function NoNotifications() {
    return (
        <VStack pt={6} flexDirection="column" gap={6}>
            <FaIcon icon={faBells} fontSize={120} color="muted.50" />
            <VStack>
                <Heading size="md">
                    You&apos;re all caught up!
                </Heading>
                <Text color="muted.500">
                    We&apos;ll notify you when there&apos;s something new.
                </Text>
            </VStack>
        </VStack>
    );
}

export function NotificationList() {
    const {
        notifications,
        isLoading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = useNotifications();

    const parentRef = useRef<HTMLDivElement>(null);

    const rowVirtualizer = useVirtualizer({
        count: notifications.length,
        getScrollElement: () => parentRef.current,
        // This is the rough height of each notification in px the
        // virtualiser reserves before the real height is measured
        estimateSize: () => 120,
        // Controls how many extra rows get rendered beyond the viewport
        overscan: 5,
    });

    const virtualItems = rowVirtualizer.getVirtualItems();

    // Calculate the bottommost rendered row's index; Used to detect the end of the
    // scroll in the user's view before it fetches more notifications
    const lastVirtualItemIndex = virtualItems.at(-1)?.index;
    const isLastItemInView = lastVirtualItemIndex !== undefined
        && lastVirtualItemIndex >= notifications.length - 1;

    useEffect(() => {
        if (isLastItemInView && hasNextPage && !isFetchingNextPage) {
            void fetchNextPage();
        }
    }, [isLastItemInView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Whether the loaded notifications overflow the drawer, used below to decide
    // whether the "You're all caught up" message should be shown
    const hasOverflow = parentRef.current !== null
        && rowVirtualizer.getTotalSize() > parentRef.current.clientHeight;

    if (isLoading) {
        return (
            <Center py={8}>
                <Spinner color="primary.500" />
            </Center>
        );
    }

    if (notifications.length === 0) {
        return <NoNotifications />;
    }

    return (
        <Box ref={parentRef} h="full" overflowY="auto">
            <Box position="relative" w="full" h={`${rowVirtualizer.getTotalSize()}px`}>
                {virtualItems.map((virtualItem) => {
                    const notification = notifications[virtualItem.index];

                    return (
                        <Box
                            key={notification.id}
                            ref={rowVirtualizer.measureElement}
                            data-index={virtualItem.index}
                            position="absolute"
                            top={0}
                            left={0}
                            w="full"
                            transform={`translateY(${virtualItem.start}px)`}
                        >
                            <NotificationItem notification={notification} />
                        </Box>
                    );
                })}
            </Box>

            <Center pt={8} pb={12}>
                {isFetchingNextPage && (
                    <Spinner color="primary.500" />
                )}

                {!hasNextPage && hasOverflow && (
                    <VStack>
                        <Text fontWeight="medium">
                            You&apos;re all caught up!
                        </Text>
                        <Text>
                            We&apos;ll notify you when there&apos;s something new.
                        </Text>
                    </VStack>
                )}
            </Center>
        </Box>
    );
}
